import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import dayjs from 'dayjs';
import { DateFormat, i18n, useForm } from "src/common";
import { InputCity, InputStreet, InputFullName, InputText, Validation, InputBirthDate, InputOIB, InputMBO } from "src/components";
import { FieldConfig, Form, FormConfig, FormRef } from "./form";

export type InsuredPersonFormItemType = 'birthDate' | 'fullName' | 'oib' | 'insuranceNumber' | 'city' | 'street' | 'houseNumber' | 'partnerContactEmail' | 'phoneNumber';
export const allInsuredPersonFormItems: Array<InsuredPersonFormItemType> = ['birthDate', 'partnerContactEmail', 'fullName', 'oib', 'insuranceNumber', 'city', 'street', 'houseNumber'];

interface InsuredPersonFormProps {
  insuredPerson: any; // TODO
  formItems?: Array<InsuredPersonFormItemType>;
  onAddressChange?: (value: any) => void;
  storedValues?: any;
}

export const InsuredPersonForm = forwardRef<FormRef, InsuredPersonFormProps>(({ insuredPerson, formItems = allInsuredPersonFormItems, onAddressChange, storedValues }, ref) => {
  const { formRef, submitForm, getValues, setFieldValue, validateForm, scrollIntoView } = useForm();
  const [insuredPersonInitialValues, setInsuredPersonInitialValues] = React.useState<any | undefined>(undefined);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      submitForm();
    },
    getValues: () => getValues(),
    setFieldValue: (fieldName: string, value: any, validation?: boolean) => setFieldValue(fieldName, value, validation),
    validateForm: (validateFields?: string[]) => validateForm(validateFields),
    scrollIntoView: () => scrollIntoView()
  }));

  const handleSubmit = (values: { [key: string]: any }) => {
    console.log('Submitted values:', values);
  };

  // Mapiranje na ulazu u formu
  const modelToForm = (data: any) => {
    const insuredPersonIV = Object.keys(data).reduce((acc: any, key: string) => {
      if (key !== "__typename") {
        const value = (!data[key].isEditable || data[key].value === null || data[key].value === "" || data[key].value === 0) && storedValues && storedValues[key] ? storedValues[key] : data[key].value;
        if (value !== null)
          acc[key] = value;
        else
          acc[key] = "";
      }
      return acc;
    }, {});
    insuredPersonIV.fullName = `${insuredPersonIV.firstName ?? ""} ${insuredPersonIV.lastName ?? ""}`.trim();
    insuredPersonIV.birthDate = insuredPersonIV.birthDate ? dayjs(insuredPersonIV.birthDate).format(DateFormat) : '';
    return insuredPersonIV;
  }

  useEffect(() => {
    const modelData = modelToForm(insuredPerson);
    const insuredPersonValues = {
      ...modelData,
      // ...storedValues
    };
    setInsuredPersonInitialValues(insuredPersonValues);
  }, [insuredPerson]);

  const fields: FieldConfig[] = [
    {
      name: 'fullName',
      label: i18n.t('components.forms.insuredPersonForm.fullName'),
      component: InputFullName,
      disabled: !(insuredPerson.firstName.isEditable && insuredPerson.lastName.isEditable),
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validFullName', message: 'Unesi ispravno ime i prezime' }
      ] as Validation[],
      onChange: (value: any) => {
        if (onAddressChange) onAddressChange({ name: value.fullName });
      }
    },
    {
      name: 'birthDate',
      label: i18n.t('components.forms.insuredPersonForm.dateOfBirth'),
      component: InputBirthDate,
      disabled: !insuredPerson.birthDate.isEditable,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validDate', message: 'Unesi ispravan datum rođenja' },
      ] as Validation[],
    },
    {
      name: 'oib',
      label: i18n.t('components.forms.insuredPersonForm.oib'),
      component: InputOIB,
      disabled: !insuredPerson.oib.isEditable,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validOIB', message: 'Netočan unos. Unesi ispravni OIB.' },
      ] as Validation[],
    },
    {
      name: 'insuranceNumber',
      label: i18n.t('components.forms.insuredPersonForm.mbo'),
      component: InputMBO,
      disabled: !insuredPerson.insuranceNumber.isEditable,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validMBO', message: 'Netočan unos. Unesi ispravni MBO.' },
      ] as Validation[],
    },
    {
      name: 'city',
      label: i18n.t('components.forms.insuredPersonForm.city'),
      component: InputCity,
      disabled: !(insuredPerson.city.isEditable && insuredPerson.cityKompareId.isEditable),
      formatValue: (values: any) => (values.city || values.zipCode) && `${values.city}, ${values.zipCode}`,
      onChange: (value: any) => {
        if (onAddressChange) onAddressChange(value);
      },
      validations: [
        { type: 'required', message: 'Potreban unos' },
      ] as Validation[],
    },
    {
      name: 'street',
      label: i18n.t('components.forms.insuredPersonForm.street'),
      component: InputStreet,
      disabled: !(insuredPerson.street.isEditable && insuredPerson.streetKompareId.isEditable),
      componentProps: {
        cityKompareId: 'cityKompareId',
      },
      onChange: (value: any) => {
        if (onAddressChange) onAddressChange(value);
      },
      onBlur: () => {
        validateForm(['street']);
      },
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validStreets', message: 'Adresa nije pronađena, pokušaj ponovno.', params: { streetsFound: 'streetsFound' } },
      ] as Validation[],
    },
    {
      name: 'houseNumber',
      label: i18n.t('components.forms.insuredPersonForm.number'),
      component: InputText,
      componentProps: {
        maxLength: 5,
        autoComplete: "new-houseNumber-lvl2"
      },
      disabled: !insuredPerson.houseNumber.isEditable,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validHouseNumber', message: 'Netočan unos. Unesi ispravni kućni broj.' },
      ] as Validation[],
      onChange: (value: any) => {
        if (onAddressChange) onAddressChange({ houseNumber: value });
      }
    },
    {
      name: 'partnerContactEmail',
      label: i18n.t('components.forms.insuredPersonForm.email'),
      component: InputText,
      componentProps: {
        type: 'email',
        maxLength: 50
      },
      disabled: !insuredPerson.partnerContactEmail.isEditable,
      validations: [
        { type: 'required', message: 'Polje je obavezno.' },
        { type: 'validMail', message: 'Unesite ispravnu e-mail adresu.' },
      ] as Validation[],
    }
  ].filter((field) => formItems.includes(field.name as InsuredPersonFormItemType));

  const formConfig: FormConfig = {
    gap: 24
  };

  if (!insuredPersonInitialValues) return null;
  return (
    <Form ref={formRef} fields={fields} config={formConfig} onSubmit={handleSubmit} initialValues={insuredPersonInitialValues} reduxName='insuredPerson' />
  )
});
